import { FC, useEffect, useRef } from 'react';
import { Box, Paper, useMediaQuery, useTheme } from '@mui/material';
import ConversationList from './components/conversationList.view';
import ChatWindow from './components/chatWindow.view';
import GroupChatList from './components/groupChatList.view';
import GroupChatWindow from './components/groupChatWindow.view';
import { useMessagingContext } from '../../contexts/messaging.context';

const MESSAGING_MOBILE_HISTORY_KEY = 'messagingMobileChat';

const MessagingView: FC = () => {
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down('md'));
  const {
    state,
    setSelectedReceiver,
    setSelectedClass,
    setSelectedConversation,
    setPendingNewConversation,
    setSelectedGroup,
  } = useMessagingContext();

  const isGroupMode = state.conversationFilter === 'groups';
  const hasChatOpen = Boolean(
    state.selectedReceiverId || state.selectedClassId || state.selectedGroupId
  );
  const prevHadChatOpenRef = useRef(false);
  const hasChatOpenRef = useRef(hasChatOpen);
  hasChatOpenRef.current = hasChatOpen;

  // Push a history entry when opening a chat on mobile so browser/device back returns to the list.
  useEffect(() => {
    if (!isMobileLayout) {
      prevHadChatOpenRef.current = hasChatOpen;
      return;
    }
    if (hasChatOpen && !prevHadChatOpenRef.current) {
      window.history.pushState(
        { [MESSAGING_MOBILE_HISTORY_KEY]: true },
        ''
      );
    }
    prevHadChatOpenRef.current = hasChatOpen;
  }, [isMobileLayout, hasChatOpen]);

  // Handle device back: clear chat selection when the pushed entry is popped.
  useEffect(() => {
    if (!isMobileLayout) return;

    const onPopState = () => {
      if (!hasChatOpenRef.current) return;
      setSelectedReceiver(null);
      setSelectedClass(null);
      setSelectedConversation(null);
      setPendingNewConversation(null);
      setSelectedGroup(null);
      prevHadChatOpenRef.current = false;
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [
    isMobileLayout,
    setSelectedReceiver,
    setSelectedClass,
    setSelectedConversation,
    setPendingNewConversation,
    setSelectedGroup,
  ]);

  const showList = !isMobileLayout || !hasChatOpen;
  const showChat = !isMobileLayout || hasChatOpen;

  return (
    <Box
      sx={{
        height: 'calc(100vh - 150px)',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        bgcolor: 'grey.100',
        borderRadius: { xs: 0, sm: 1 },
        overflow: 'hidden',
        px: { xs: 0, sm: 1 },
        py: { xs: 1, sm: 1 },
      }}
      data-testid="messaging-view"
    >
      {showList && (
        <Paper
          elevation={0}
          sx={{
            width: { xs: '100%', md: '35%' },
            minWidth: { xs: 'auto', md: 300 },
            maxWidth: { xs: '100%', md: 400 },
            height: { xs: '100%', md: '100%' },
            minHeight: { xs: 0, md: 'auto' },
            flex: { xs: 1, md: '0 0 auto' },
            display: 'flex',
            flexDirection: 'column',
          }}
          data-testid="messaging-conversation-list-container"
        >
          {isGroupMode ? <GroupChatList /> : <ConversationList />}
        </Paper>
      )}

      {showChat && (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            height: { xs: '100%', md: '100%' },
          }}
          data-testid="messaging-chat-window-container"
        >
          {isGroupMode ? (
            <GroupChatWindow />
          ) : (
            <ChatWindow messagingMobileHistoryKey={MESSAGING_MOBILE_HISTORY_KEY} />
          )}
        </Box>
      )}
    </Box>
  );
};

export default MessagingView;
